// BootScene — pre-warm. Builds the few procedural textures the game reuses (a soft particle),
// QUEUES the M2 audio + (when present) generated art into the loader, then routes to Menu — or
// straight into a Game board if ?play=1 was passed. Audio/art loads are non-fatal: if a file is
// missing or fails to decode the play() calls become silent no-ops, so the M0/M1 gate is unaffected.
import Phaser from 'phaser';
import { PALETTE, registerLoadedArt, loadArtInto, loadMuncherSheetsInto, createMuncherAnims } from '../keys';
import { REG, readBoot } from '../registryKeys';
import { audio } from '../audio';

export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload(): void {
    // M2 AUDIO: queue every music/SFX/VO file once, here, so they're warm before the first board.
    // (Loader errors are swallowed by Phaser; the bus also guards every play() against missing keys.)
    audio().loadInto(this);
    // M2 ART: queue any accepted generated images. Empty until art lands -> no-op, gate-safe.
    loadArtInto(this);
    // Animated muncher spritesheets (AutoSprite). Non-fatal: a failed decode leaves the geometric
    // muncher fallback in place (muncherArtReady() stays false), so the gate is unaffected.
    loadMuncherSheetsInto(this);
  }

  create(): void {
    // A reusable 1px white texture for cheap particle/burst use if needed by later milestones.
    if (!this.textures.exists('particle')) {
      const g = this.make.graphics({ x: 0, y: 0 });
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 6, 6);
      g.generateTexture('particle', 6, 6);
      g.destroy();
    }

    // Register the texture-key indirection for ONLY the art that actually decoded into the cache.
    // A failed decode (or a headless run that couldn't fetch) leaves that key procedural — hasTex()
    // stays false for it, so the renderer keeps the M0/M1 fallback and the board never breaks.
    registerLoadedArt(this);
    // Build the muncher anims for whichever sheets decoded (global anim manager; idempotent).
    createMuncherAnims(this);

    this.cameras.main.setBackgroundColor(PALETTE.bg);

    const boot = readBoot(this);
    if (boot.autoPlay && (boot.topic || boot.daily) && !boot.debug) {
      // M3 SHARE DEEP LINK (?topic=…&seed=…&play=1): a friend opened a "beat my score" link. Route
      // through the Menu so it FETCHES the live board for that topic (seed fallback if offline) and
      // flags the run as the right kind — rather than only resolving the seed pack. The capture/eyes
      // harness uses ?debug=1, which stays on the deterministic straight-to-board path below.
      // A TOPIC-LESS daily deep link (?play=1&daily=1, e.g. a naked shared URL) also routes here so it
      // goes through MenuScene.playDaily → getDaily (the topic-of-day) instead of a topic='' seed board.
      this.scene.start('Menu', { deepLinkTopic: boot.topic, deepLinkDaily: boot.daily });
    } else if (boot.autoPlay) {
      // Deterministic straight-to-board for the screenshot/eyes harness (?play=1&debug=1) and the
      // no-topic autoplay case.
      this.scene.start('Game', {
        topic: boot.topic,
        seed: boot.seed,
        ai: boot.ai,
        boardIndex: (this.registry.get(REG.boardIndex) as number) ?? 0,
      });
    } else if (boot.debug) {
      this.scene.start('Menu');
    } else {
      this.scene.start('Splash');
    }
  }
}
